// Fund view. Managers get the admin surface (NAV, units, investors, ledgers,
// + strike / subscribe / redeem actions). Clients get their own investor
// position (units, NAV/unit, value).

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PageHeader, Pill, Stat } from '../../components/shell';
import { NavSparkline } from '../../components/NavSparkline';
import { CreateInvestorForm } from '../../components/CreateInvestorForm';
import { FundSubnav } from './FundLayout';
import { fmtBase, fmtPct, fmtUnitPrice, fmtUnitPriceOps } from '../../lib/format';
import { nowLocalForDatetimeMax, isFutureWhen, isBeforeInception } from '../../lib/datetime';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

const fmtUnits = (s: string | number) =>
  Number(s).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 8 });
const fmtDT = (iso: string) => new Date(iso).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
// FV2.7 — symmetric signed money: sign glyph BEFORE the currency symbol
// (+$12.3456 / −$12.3456, U+2212), matching fmtUSDcompact's convention;
// fmtBase alone renders negatives as '$-12.3456'.
const signedBase = (x: any, ccy: string | undefined, decimals = 4) => {
  const v = Number(x);
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return sign + fmtBase(Math.abs(v), ccy, decimals);
};

export default function Fund() {
  const { user } = useAuth();
  if (user?.role === 'client') return <InvestorView />;
  return <ManagerView />;
}

// ─── Manager admin ──────────────────────────────────────────────────────────
function ManagerView() {
  const [funds, setFunds] = useState<any[] | null>(null);
  const [fundId, setFundId] = useState<string | null>(null);
  const [investors, setInvestors] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  // `payments` stays for the FEE HEALTH metric (totalPaid + sweep count); the
  // FEE PAYMENTS history table moved to /fund/:fundId/activity (16.d.4.4).
  const [payments, setPayments] = useState<any[]>([]);
  // Landing keeps `accruals` only for the FEE HEALTH day-count (.total); the
  // full ledger + MGMT/PERF toggle moved to /fund/:fundId/accruals (16.d.4.3).
  const [accruals, setAccruals] = useState<any | null>(null);
  const [hwms, setHwms] = useState<any | null>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fund = useMemo(() => funds?.find(f => f.fund_id === fundId) || null, [funds, fundId]);

  const loadFunds = async () => {
    const f = await api<any[]>('/ops/funds');
    setFunds(f);
    if (f.length && !fundId) setFundId(f[0].fund_id);
  };
  const loadDetail = async (id: string) => {
    // Landing needs: investors (roster), nav-strike (NAV sparkline), and
    // manager-fee-payments (FEE HEALTH totalPaid). The subscriptions /
    // redemptions / crystallizations ledgers moved to /fund/:id/activity.
    const [inv, h, p] = await Promise.all([
      api<any[]>(`/ops/funds/${id}/investors`),
      api<any[]>(`/ops/funds/${id}/nav-strike`),
      api<any[]>(`/ops/funds/${id}/manager-fee-payments`),
    ]);
    setInvestors(inv); setHistory(h); setPayments(p);
  };

  useEffect(() => { loadFunds().catch(() => setErr('Failed to load funds.')); }, []);
  useEffect(() => { if (fundId) loadDetail(fundId).catch(() => setErr('Failed to load fund detail.')); }, [fundId]);
  useEffect(() => { api<any[]>('/ops/clients').then(setClients).catch(() => {}); }, []);
  useEffect(() => {
    if (!fundId) return;
    // Only `.total` (the accrual day-count) is read here, by FEE HEALTH —
    // limit=1 keeps it cheap. The full ledger lives at /fund/:id/accruals.
    api<any>(`/ops/funds/${fundId}/accruals?kind=management&limit=1`)
      .then(setAccruals)
      .catch(() => setAccruals(null));
  }, [fundId]);
  const loadHwms = (id: string) =>
    api<any>(`/ops/funds/${id}/hwms`).then(setHwms).catch(() => setHwms(null));
  useEffect(() => { if (fundId) loadHwms(fundId); }, [fundId]);
  // Map investor_id → current HWM for the roster table.
  const hwmByInvestor = useMemo(() => {
    const map: Record<string, string> = {};
    (hwms?.rows || []).forEach((r: any) => { map[r.investor_id] = r.hwm_per_unit_base; });
    return map;
  }, [hwms]);

  // A first subscription seeds a new HWM row server-side (record_first_
  // subscription_hwm) — refresh() must re-fetch hwms too, or a brand-new
  // investor's HWM/unit cell shows "—" until a manual page reload happens
  // to re-run the fundId-keyed effect above.
  const refresh = async () => {
    await loadFunds();
    if (fundId) await Promise.all([loadDetail(fundId), loadHwms(fundId)]);
  };

  if (err) return <div className="view container"><p className="serif-body" style={{ color: 'var(--oxblood)' }}>{err}</p></div>;
  if (!funds) return <Loading />;
  if (!funds.length) return (
    <div className="view container">
      <PageHeader kicker="GET STARTED" title={<><em>Create</em> your fund.</>} />
      <p className="serif-body" style={{ fontSize: 16, marginTop: 24, maxWidth: '54ch', color: 'var(--ink-2)' }}>
        Your fund is the home for everything you manage here — its terms, the accounts
        it tracks, its investors, and its NAV. Set it up in a few guided steps, then
        start striking NAV and recording subscriptions.
      </p>
      <Link to="/fund/new" className="btn" style={{ textDecoration: 'none', marginTop: 28, display: 'inline-block' }}>
        Create your fund <span className="btn-arrow">→</span>
      </Link>
    </div>
  );

  return (
    <div className="view container">
      <PageHeader
        kicker="FUND"
        title={<>{fund?.name || 'Fund'}<em>.</em></>}
        meta={<span className="label-s">AS OF {fund ? fmtDT(fund.as_of) : '—'}</span>}
      />
      {/* Phase 8.b.15 — manager-side fund switcher. Renders when the */}
      {/* tenant has > 1 fund; lets the manager flip between them.    */}
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        {funds.length > 1 && (
          <label className="label-s" style={{ display: 'inline-block' }}>
            FUND&nbsp;
            <select
              className="num"
              value={fundId || ''}
              onChange={e => setFundId(e.target.value)}
            >
              {funds.map(f => (
                <option key={f.fund_id} value={f.fund_id}>{f.name}</option>
              ))}
            </select>
          </label>
        )}
        <Link to="/fund/new" className="btn-link">+ New fund</Link>
      </div>

      {/* Persistent fund subnav (16.d.4.5b) — same component the sub-routes use
          (FundLayout); "Overview" is active here on the landing. Supersedes the
          per-section link-out blocks that used to live further down this page. */}
      {fundId && <div style={{ marginTop: 20 }}><FundSubnav fundId={fundId} /></div>}

      <div className="agg-strip" style={{ ['--cols' as any]: 4 } as any}>
        {/* TRES Phase 7 / n8 cutover: render every money cell in the fund's */}
        {/* base currency. Backend (FundOverview, 7.b) sends both `*_usd` and */}
        {/* `*_base` keys; pick the `_base` figure for non-USD funds.        */}
        {/* FV2.4 — fund-level valuation_pending gates the asset-side stats */}
        {/* (Number(null) would render a fabricated $0.00); accrued + units  */}
        {/* stay real — mirrors the roster's per-investor em-dash treatment. */}
        <div className="agg-cell"><Stat label="NET ASSET VALUE" value={!fund ? '—' : fund.valuation_pending ? '—' : fmtBase(Number(fund.base_currency === 'USD' ? fund.total_nav_usd : fund.total_nav_base), fund.base_currency)} sub={!fund ? '' : fund.valuation_pending ? 'VALUATION PENDING' : `GROSS ${fmtBase(Number(fund.base_currency === 'USD' ? fund.gross_nav_usd : fund.gross_nav_base), fund.base_currency)}`} /></div>
        <div className="agg-cell"><Stat label="NAV PER UNIT" value={!fund ? '—' : fund.valuation_pending ? '—' : fmtUnitPriceOps(Number(fund.base_currency === 'USD' ? fund.nav_per_unit : fund.nav_per_unit_base), fund.base_currency)} sub={!fund ? '' : fund.valuation_pending ? 'VALUATION PENDING' : 'NET OF FEES'} /></div>
        <div className="agg-cell"><Stat label="UNITS OUTSTANDING" value={fund ? fmtUnits(fund.units_outstanding) : '—'} sub={`${fund?.investor_count ?? 0} INVESTORS`} /></div>
        <div className="agg-cell"><Stat label="ACCRUED FEES" value={fund ? fmtBase(Number(fund.base_currency === 'USD' ? fund.accrued_fees_usd : fund.accrued_fees_base), fund.base_currency) : '—'} sub="MGMT + PERF (UNPAID)" /></div>
      </div>

      {history.length >= 2 && (
        <section style={{ marginTop: 40 }}>
          <div className="kicker-row"><span className="kicker">NAV PER UNIT · HISTORY</span></div>
          <NavSparkline series={[...history].reverse().map(h => ({ x: h.struck_at, y: Number(fund?.base_currency === 'USD' ? h.nav_per_unit : (h.nav_per_unit_base ?? h.nav_per_unit)) }))} currency={fund?.base_currency || 'USD'} />
        </section>
      )}

      {/* FV2.7 — valuation rows + the FV2.6b correction impact report.
          key={fundId}: the panel state must die with a fund switch, or the
          old fund's impactId orphans a panel under the new fund's table. */}
      {fund && fundId && history.length > 0 && (
        <ValuationHistorySection
          key={fundId}
          history={history} baseCurrency={fund.base_currency} fundId={fundId}
        />
      )}

      {/* Phase 8.b.14 — fee health metric (manager-only). Consolidates    */}
      {/* what's already on the page into a single operator-readable line. */}
      {fund && (accruals?.total != null || payments.length > 0) && (() => {
        const totalPaid = payments.reduce((s, p) => s + Number(p.amount_base || 0), 0);
        const currentlyAccrued = Number(fund.base_currency === 'USD' ? fund.accrued_fees_usd : fund.accrued_fees_base) || 0;
        const lifetimeAccrued = currentlyAccrued + totalPaid;
        const accrualDays = accruals?.total ?? 0;
        const unpaidPct = lifetimeAccrued > 0 ? (currentlyAccrued / lifetimeAccrued) * 100 : 0;
        return (
          <section style={{ marginTop: 32 }}>
            <div className="kicker-row"><span className="kicker">FEE HEALTH</span></div>
            <p className="serif-body" style={{ fontSize: 14, marginTop: 8, color: 'var(--ink-2)' }}>
              {fmtBase(lifetimeAccrued, fund.base_currency)} accrued lifetime across {accrualDays} day{accrualDays === 1 ? '' : 's'}
              {' · '}{fmtBase(totalPaid, fund.base_currency)} paid in {payments.length} sweep{payments.length === 1 ? '' : 's'}
              {' · '}<span className="num">{unpaidPct.toFixed(1)}%</span> unpaid
            </p>
          </section>
        );
      })()}

      {/* 15.2.f.4 — unaccounted external value WARN (non-blocking). Value that */}
      {/* landed on the fund's wallets with no units issued dilutes existing    */}
      {/* investors until cured by a subscription minted at the pre-arrival NAV. */}
      {Array.isArray(fund?.unaccounted_external) && fund.unaccounted_external.length > 0 && (
        <section style={{ marginTop: 40 }}>
          <div className="kicker-row"><span className="kicker">UNACCOUNTED EXTERNAL VALUE</span></div>
          <p className="serif-body" style={{ marginTop: 8, fontSize: 14, color: 'var(--ink-2)', maxWidth: '66ch' }}>
            Value arrived on this fund's wallets with no units issued. It raises NAV per unit and
            dilutes existing investors until you record it as a subscription — which mints units at
            the pre-arrival NAV, so no one is diluted.
          </p>
          <table className="tbl" style={{ marginTop: 12 }}>
            <thead><tr><th>ASSET</th><th data-align="right">VALUE</th><th>WHEN (UTC)</th><th>REASON</th><th>CURE</th></tr></thead>
            <tbody>
              {fund.unaccounted_external.map((row: any) => (
                <ExternalCureRow
                  key={row.id} fundId={fundId!} row={row} clients={clients}
                  baseCurrency={fund?.base_currency || 'USD'} onDone={refresh}
                />
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Admin actions */}
      <section style={{ marginTop: 40, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 24 }}>
        <ActionCard title="STRIKE NAV" desc="Record a NAV valuation at now.">
          <StrikeNavButton fundId={fundId!} busy={busy} setBusy={setBusy} onDone={refresh} setErr={setErr} baseCurrency={fund?.base_currency} />
        </ActionCard>
        {/* key={fundId}: remount on fund switch so an inline-created investor
            (SubscribeCard.extra/selection) can't carry over to the wrong fund. */}
        <SubscribeCard key={fundId} fundId={fundId!} clients={clients} baseCurrency={fund?.base_currency || 'USD'} onDone={refresh} setErr={setErr} />
        <RedeemCard fundId={fundId!} investors={investors} baseCurrency={fund?.base_currency}
                    onDone={refresh} setErr={setErr} />
        <CrystallizeCard fundId={fundId!} onDone={refresh} setErr={setErr} />
        <FeePaymentCard fundId={fundId!} baseCurrency={fund?.base_currency || 'USD'} onDone={refresh} setErr={setErr} />
        <FixValuationCard fundId={fundId!} baseCurrency={fund?.base_currency || 'USD'} onDone={refresh} />
      </section>

      {/* Phase 8.b.12 — close-fund button. Refuses if units > 0 (server- */}
      {/* side guard); a one-way state change. Inline 409 error handling. */}
      {fundId && fund?.status === 'open' && (
        <section style={{ marginTop: 48 }}>
          <div className="kicker-row"><span className="kicker">DANGER ZONE</span></div>
          <p className="serif-body" style={{ marginTop: 12, fontSize: 14 }}>
            Closing a fund freezes it permanently. Redeem all investors to zero before closing.
          </p>
          <button
            className="btn"
            style={{ marginTop: 12 }}
            disabled={busy}
            onClick={async () => {
              if (!confirm('Close this fund? This is a one-way operation.')) return;
              setBusy(true); setErr(null);
              try {
                await api(`/ops/funds/${fundId}/close`, { method: 'POST', body: {} });
                await refresh();
              } catch (e: any) {
                if (e?.status === 409) {
                  setErr(e?.payload?.detail || 'Fund cannot be closed in current state.');
                } else {
                  setErr(e?.message || 'Close failed.');
                }
              } finally { setBusy(false); }
            }}
          >Close fund</button>
        </section>
      )}
      {fund?.status === 'closed' && (
        <section style={{ marginTop: 48 }}>
          <div className="kicker-row"><span className="kicker">FUND CLOSED</span></div>
          <p className="serif-body" style={{ marginTop: 12, fontSize: 14, color: 'var(--ink-3)' }}>
            This fund has been closed. Subscriptions and redemptions are no longer accepted.
          </p>
        </section>
      )}

      {/* Investors */}
      <section style={{ marginTop: 48 }}>
        <div className="kicker-row"><span className="kicker">INVESTORS</span></div>
        <table className="tbl" style={{ marginTop: 12 }}>
          <thead><tr><th>INVESTOR</th><th>SUBSCRIBED</th><th data-align="right">UNITS</th><th data-align="right">OWNERSHIP</th><th data-align="right">HWM / UNIT</th><th data-align="right">CONTRIBUTED</th><th data-align="right">VALUE</th></tr></thead>
          <tbody>
            {investors.map(i => (
              <tr key={i.investor_id}>
                <td><Link to={`/fund/${fundId}/investors/${i.investor_id}`} style={{ color: 'var(--ink)' }}>{i.investor_name}</Link></td>
                {/* Operator-review ask (2026-07): show when the investor first */}
                {/* joined, not just their current units/value. */}
                <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>{i.first_subscribed_at ? fmtDT(i.first_subscribed_at) : '—'}</td>
                <td data-align="right" className="num">{fmtUnits(i.units)}</td>
                <td data-align="right" className="num">{fmtPct(Number(i.ownership_pct))}</td>
                <td data-align="right" className="num">{hwmByInvestor[i.investor_id] ? fmtUnitPriceOps(Number(hwmByInvestor[i.investor_id]), fund?.base_currency) : '—'}</td>
                <td data-align="right" className="num">{fmtBase(Number(fund?.base_currency === 'USD' ? i.contributed_usd : i.contributed_base), fund?.base_currency)}</td>
                {/* OB.6 — value is null until a strike covers the investor; show */}
                {/* an em-dash, matching the detail drill-down's pending state.    */}
                <td data-align="right" className="num">{i.valuation_pending ? '—' : fmtBase(Number(fund?.base_currency === 'USD' ? i.value_usd : i.value_base), fund?.base_currency)}</td>
              </tr>
            ))}
            {!investors.length && <tr><td colSpan={7} className="label-s" style={{ color: 'var(--ink-3)' }}>No investors yet.</td></tr>}
          </tbody>
        </table>
      </section>


    </div>
  );
}

function ActionCard({ title, desc, children }: { title: string; desc: string; children: any }) {
  return (
    <div style={{ border: 'var(--hairline)', borderColor: 'var(--rule)', padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="label">{title}</div>
      <p className="serif-body" style={{ margin: 0, fontSize: 13, color: 'var(--ink-3)' }}>{desc}</p>
      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  );
}

// 15.2.f.4 — one unaccounted-external-value WARN row + an expandable inline cure
// form. The operator picks the investor the value belongs to + an amount
// (pre-filled with the row's value_usd as a suggestion; they type the real
// base-currency figure), POSTs the cure (V-excluded NAV mint), then refresh()
// drops the now-cured row off the WARN. value_usd is always USD (the inbound's
// captured mark); the amount the operator enters is in the fund's base currency.
function ExternalCureRow({ fundId, row, clients, baseCurrency, onDone }: any) {
  const [open, setOpen] = useState(false);
  const [investor, setInvestor] = useState('');
  // Pre-fill the suggestion ONLY for USD funds — value_usd is a USD mark, and
  // the amount is interpreted in fund.base_currency, so pre-filling USD into a
  // EUR field would mint off a wrong amount. Non-USD funds start empty (the
  // placeholder shows the base currency); the row's VALUE column shows the USD ref.
  const [amount, setAmount] = useState(baseCurrency === 'USD' ? row.value_usd : '');
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setLocalErr(null);
    try {
      await api(`/ops/funds/${fundId}/external-cure`, {
        method: 'POST',
        body: { external_row_id: row.id, investor_id: investor, amount: String(amount) },
      });
      await onDone();  // the cured row drops off fund.unaccounted_external
    } catch (e: any) {
      const kind = e?.payload?.error_kind;
      if (kind === 'already_cured') { await onDone(); return; }  // already cured (race) → drop the row
      setLocalErr(
        kind === 'no_valuation_for_dealing_date'
          ? 'No NAV struck in the dealing window. Strike NAV first, then retry.'
          : (e?.payload?.detail || e?.message || 'Cure failed.'),
      );
    } finally { setBusy(false); }
  };

  return (
    <>
      <tr>
        <td>{row.asset}</td>
        <td data-align="right" className="num">{fmtBase(Number(row.value_usd), 'USD')}</td>
        <td className="num" style={{ fontSize: 12 }}>
          {new Date(row.occurred_at).toISOString().slice(0, 16).replace('T', ' ')}
        </td>
        <td className="serif-body" style={{ fontSize: 13, color: 'var(--ink-2)' }}>{row.reason}</td>
        <td>
          <button
            type="button"
            onClick={() => { setOpen(o => !o); setLocalErr(null); }}
            className="label-s"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', font: 'inherit' }}
          >{open ? 'Cancel' : 'Cure →'}</button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '8px 0' }}>
              {/* Firm-wide `clients` (not the fund roster) is intentional + matches */}
              {/* SubscribeCard: the external value may belong to a NOT-YET-subscribed */}
              {/* investor whose deposit is being formalized here.                     */}
              <select
                className="field-input" value={investor}
                onChange={e => { setInvestor(e.target.value); setLocalErr(null); }}
                style={{ maxWidth: 220 }}
              >
                <option value="">Select investor…</option>
                {clients.map((c: any) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
              </select>
              <input
                className="field-input" type="number" value={amount}
                onChange={e => { setAmount(e.target.value); setLocalErr(null); }}
                placeholder={`Amount ${baseCurrency}`} style={{ maxWidth: 180 }}
              />
              <button className="btn" disabled={busy || !investor || !amount} onClick={submit}>
                Record cure <span className="btn-arrow">→</span>
              </button>
            </div>
            {localErr && <p className="label-s" style={{ color: 'var(--oxblood)', marginTop: 4, marginBottom: 8 }}>{localErr}</p>}
          </td>
        </tr>
      )}
    </>
  );
}

function StrikeNavButton({ fundId, busy, setBusy, onDone, setErr, baseCurrency }: any) {
  const [overrideNav, setOverrideNav] = useState('');
  const [ok, setOk] = useState<string | null>(null);
  const [localErr, setLocalErr] = useState<string | null>(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label className="field-label" style={{ fontSize: 11 }}>
          NAV PER UNIT ({baseCurrency || 'USD'}) — OPTIONAL
        </label>
        <input
          className="field-input num"
          type="number" min="0.000001" step="any"
          value={overrideNav}
          onChange={e => setOverrideNav(e.target.value)}
          placeholder="Leave blank to calculate from holdings"
          style={{ fontSize: 13 }}
        />
        <div style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)' }}>
          Enter a value to override — useful when holdings data is unavailable.
        </div>
      </div>
      <button className="btn" disabled={busy} onClick={async () => {
        setBusy(true); setOk(null); setLocalErr(null); setErr(null);
        const body: Record<string, unknown> = {};
        if (overrideNav.trim()) body.override_nav_per_unit = overrideNav.trim();
        try {
          const v = await api<any>(`/ops/funds/${fundId}/nav-strike`, { method: 'POST', body });
          await onDone();
          const price = v.nav_per_unit_base ?? v.nav_per_unit ?? '0';
          setOk(`NAV struck — ${fmtUnitPriceOps(Number(price), baseCurrency)}/unit`);
          setOverrideNav('');
        } catch (e: any) {
          const msg = e?.payload?.detail || e?.payload?.error || e?.message || 'Strike failed.';
          setLocalErr(msg);
        } finally { setBusy(false); }
      }}>Strike now</button>
      {ok && <div style={{ fontSize: 12, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)' }}>{ok}</div>}
      {localErr && <div style={{ fontSize: 12, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--oxblood)' }}>{localErr}</div>}
    </div>
  );
}

function SubscribeCard({ fundId, clients, baseCurrency, onDone, setErr }: any) {
  const [investor, setInvestor] = useState('');
  const [amount, setAmount] = useState('');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  // 16.e.1 — create a new investor inline (then auto-select to subscribe), so a
  // manager isn't forced back through the /fund/new wizard to add a client. The
  // created investor is account-less until this subscription registers it.
  const [creating, setCreating] = useState(false);
  const [extra, setExtra] = useState<{ id: string; name: string }[]>([]);
  // Earliest existing subscription on this fund — a new one can't be dated
  // before it (see _reject_before_fund_inception on the server): backdating
  // ahead of the current earliest investor would hand out the one-time
  // "first investor" seed price to a second person while the original
  // earliest investor's frozen record stays stale. Fetched once per fund
  // since it only changes when a new earliest investor is actually added.
  const [earliestSubAt, setEarliestSubAt] = useState<string | null>(null);
  useEffect(() => {
    if (!fundId) return;
    api<any[]>(`/ops/funds/${fundId}/subscriptions`).then(rows => {
      const dates = (rows || []).map(r => r.effective_at).filter(Boolean).sort();
      setEarliestSubAt(dates[0] || null);
    }).catch(() => setEarliestSubAt(null));
  }, [fundId]);
  // Live NAV preview. Fires the preview endpoint whenever `when` changes so
  // the operator sees the NAV/unit their POST will price at BEFORE clicking
  // Issue units. The preview (and the POST itself) now auto-strikes when no
  // NAV has been struck in the dealing window yet, so there's no "strike
  // NAV first" blocker to render here — the preview always resolves to a
  // number, live-computed if nothing's been struck.
  const [preview, setPreview] = useState<any | null | 'pending' | 'error'>(null);
  useEffect(() => {
    if (!fundId) { setPreview(null); return; }
    let cancelled = false;
    const handle = setTimeout(async () => {
      if (cancelled) return;
      setPreview('pending');
      const q = when ? `?effective_at=${encodeURIComponent(new Date(when).toISOString())}` : '';
      try {
        const r = await api<any>(`/ops/funds/${fundId}/subscriptions/preview${q}`);
        if (!cancelled) setPreview(r);
      } catch (e: any) {
        if (!cancelled) setPreview('error');
      }
    }, 250);  // 250ms debounce on `when` keystrokes
    return () => { cancelled = true; clearTimeout(handle); };
  }, [fundId, when]);
  // Phase 7: placeholder follows the fund's base currency so the
  // operator never types USD into an EUR-base fund.
  const ccy = (baseCurrency || 'USD').toUpperCase();
  return (
    <ActionCard title="RECORD SUBSCRIPTION" desc="Assign units when capital comes in.">
      <select className="field-input" value={investor} onChange={e => setInvestor(e.target.value)}>
        <option value="">Select investor…</option>
        {clients.map((c: any) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
        {extra.filter(x => !clients.some((c: any) => c.id === x.id))
              .map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
      </select>
      {creating ? (
        <CreateInvestorForm
          onCreated={(inv) => { setExtra(e => [...e, inv]); setInvestor(inv.id); setCreating(false); }}
          onCancel={() => setCreating(false)}
        />
      ) : (
        <button type="button" className="btn-link" style={{ alignSelf: 'flex-start' }}
                onClick={() => setCreating(true)}>+ New investor</button>
      )}
      <input className="field-input" type="number" placeholder={`Amount ${ccy}`} value={amount} onChange={e => setAmount(e.target.value)} />
      <input className="field-input" type="datetime-local" value={when} max={nowLocalForDatetimeMax()}
             onChange={e => setWhen(e.target.value)} />
      {isFutureWhen(when) && (
        <p className="label-s" style={{ marginTop: 6, color: 'var(--oxblood)' }}>
          DATE/TIME CANNOT BE IN THE FUTURE.
        </p>
      )}
      {!isFutureWhen(when) && isBeforeInception(when, earliestSubAt) && (
        <p className="label-s" style={{ marginTop: 6, color: 'var(--oxblood)' }}>
          DATE CANNOT BE EARLIER THAN THE FUND'S EARLIEST INVESTOR
          ({earliestSubAt ? fmtDT(earliestSubAt) : ''}).
        </p>
      )}
      {preview && typeof preview === 'object' && (
        <p className="label-s" style={{ marginTop: 6, color: 'var(--ink-3)' }}>
          PRICES AT {preview.valuation_pending ? 'VALUATION PENDING' : `${fmtUnitPriceOps(Number(preview.nav_per_unit), baseCurrency)}/UNIT`} &middot;
          DEALING {String(preview.dealing_basis || '').toUpperCase().replace(/_/g, ' ')} &middot;
          DATE {preview.dealing_date || '—'}
        </p>
      )}
      {preview === 'pending' && (
        <p className="label-s" style={{ marginTop: 6, color: 'var(--ink-3)' }}>Computing preview…</p>
      )}
      {preview === 'error' && (
        <p className="label-s" style={{ marginTop: 6, color: 'var(--oxblood)' }}>PREVIEW UNAVAILABLE.</p>
      )}
      <button className="btn"
              disabled={busy || !investor || !amount || isFutureWhen(when) || isBeforeInception(when, earliestSubAt)}
              onClick={async () => {
        setBusy(true); setErr(null);
        try {
          await api(`/ops/funds/${fundId}/subscriptions`, { method: 'POST', body: {
            // Phase 7 — `amount` is interpreted in the fund's base currency.
            investor_id: investor, amount,
            ...(when ? { effective_at: new Date(when).toISOString() } : {}),
          }});
          setInvestor(''); setAmount(''); setWhen(''); await onDone();
        } catch (e: any) {
          setErr(e?.payload?.detail || e?.message || 'Subscription failed.');
        } finally { setBusy(false); }
      }}>Issue units</button>
    </ActionCard>
  );
}

function RedeemCard({ fundId, investors, baseCurrency, onDone, setErr }: any) {
  const [investor, setInvestor] = useState('');
  const [units, setUnits] = useState('');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  // 22.e.3 — live preview of the deal, mirroring SubscribeCard. Until now this
  // card had no preview at all: the operator clicked Redeem units blind to the
  // price, and (since RULE-FEE-WINDUP) blind to a management fee that would be
  // withheld from the payout. James asked for exactly this — the fee "can be
  // calculated ahead of time and doesn't need to be calculated after the fact".
  //
  // Keyed on investor + units too, not just the date: whether a deal is
  // TERMINAL depends on both, and that is what decides whether a fee is
  // withheld at all.
  const [preview, setPreview] = useState<any | null | 'pending' | 'error'>(null);
  useEffect(() => {
    if (!fundId || !investor || !units || Number(units) <= 0) { setPreview(null); return; }
    let cancelled = false;
    const handle = setTimeout(async () => {
      if (cancelled) return;
      setPreview('pending');
      const q = new URLSearchParams({ investor_id: investor, units_redeemed: units });
      if (when) q.set('effective_at', new Date(when).toISOString());
      try {
        const r = await api<any>(`/ops/funds/${fundId}/redemptions/preview?${q}`);
        if (!cancelled) setPreview(r);
      } catch {
        if (!cancelled) setPreview('error');
      }
    }, 250);  // 250ms debounce, matching SubscribeCard
    return () => { cancelled = true; clearTimeout(handle); };
  }, [fundId, investor, units, when]);
  // Has a settled preview rendered for the current inputs? Keeps the result
  // mounted while the next request is in flight.
  const previewShown = useRef(false);
  useEffect(() => {
    if (preview && typeof preview === 'object') previewShown.current = true;
    if (preview === null) previewShown.current = false;
  }, [preview]);
  const ccy = (baseCurrency || 'USD').toUpperCase();
  return (
    <ActionCard title="RECORD REDEMPTION" desc="Redeem units when capital goes out.">
      <select className="field-input" value={investor} onChange={e => setInvestor(e.target.value)}>
        <option value="">Select investor…</option>
        {/* 16.d.3.1 — only members holding units can redeem; a 0-unit linked member is a nonsensical target (server rejects as oversell). */}
        {investors.filter((i: any) => Number(i.units) > 0).map((i: any) => <option key={i.investor_id} value={i.investor_id}>{i.investor_name} ({fmtUnits(i.units)} u)</option>)}
      </select>
      <input className="field-input" type="number" placeholder="Units to redeem" value={units} onChange={e => setUnits(e.target.value)} />
      <input className="field-input" type="datetime-local" value={when} max={nowLocalForDatetimeMax()}
             onChange={e => setWhen(e.target.value)} />
      {isFutureWhen(when) && (
        <p className="label-s" style={{ marginTop: 6, color: 'var(--oxblood)' }}>
          DATE/TIME CANNOT BE IN THE FUTURE.
        </p>
      )}
      {preview && typeof preview === 'object' && !preview.refused_reason && (
        <>
          <p className="label-s" style={{ marginTop: 6, color: 'var(--ink-3)' }}>
            PRICES AT {preview.valuation_pending
              ? 'VALUATION PENDING'
              : `${fmtUnitPriceOps(Number(preview.nav_per_unit_base), ccy)}/UNIT`} &middot;
            DEALING {String(preview.dealing_basis || '').toUpperCase().replace(/_/g, ' ')} &middot;
            DATE {preview.dealing_date || '—'}
          </p>
          {preview.gross_base !== null && (
            <p className="label-s font-tabular" style={{ marginTop: 4, color: 'var(--ink-3)' }}>
              GROSS {fmtBase(Number(preview.gross_base), ccy)} &middot;
              NET PAYOUT {fmtBase(Number(preview.net_payout_base), ccy)}
            </p>
          )}
          {/* Only on the TERMINAL deal. RULE-FEE-WINDUP's scope is the last
              unitholder out, and that scope is a READING of "the final investor"
              rather than the manager's words — so this line must never appear on
              an ordinary redemption and must not imply that it could. */}
          {/* Gated on is_terminal ALONE, not on a non-zero fee. A terminal deal
              with nothing withheld is a meaningful, checkable statement — and
              when the payout cannot bear the fee the withholding clamps to zero,
              so a `> 0` gate would drop the wind-up notice on exactly the case
              where the shortfall warning below fires. Condition first, then
              consequence: every other middot on this card joins a label to a
              value, so a trailing bare assertion scans as a field with no value. */}
          {preview.is_terminal && (
            <p className="label-s font-tabular" style={{ marginTop: 4, color: 'var(--ink-3)' }}>
              LAST UNITHOLDER OUT &middot;
              FINAL MANAGEMENT FEE WITHHELD {fmtBase(Number(preview.final_mgmt_fee_base), ccy)}
            </p>
          )}
          {/* Scoped by is_terminal, not merely by the backend key's presence:
              this is the more alarming of the two lines, so its scope must rest
              on the flag the rule is defined by rather than on a payload shape. */}
          {preview.is_terminal && preview.windup?.windup_fee_uncollected_base && (
            <p className="label-s font-tabular" style={{ marginTop: 4, color: 'var(--oxblood)' }}>
              {/* NOT "cannot be collected": the accrual rows are written
                  regardless, so the receivable is live and the manager may
                  still collect it. Say what happened, not what is extinguished. */}
              {fmtBase(Number(preview.windup.windup_fee_uncollected_base), ccy)} OF THE FEE
              EXCEEDS THE PAYOUT AND WAS NOT WITHHELD — IT REMAINS OUTSTANDING.
            </p>
          )}
        </>
      )}
      {preview && typeof preview === 'object' && preview.refused_reason && (
        <p className="label-s" style={{ marginTop: 6, color: 'var(--oxblood)' }}>
          {String(preview.refused_reason).toUpperCase()}
        </p>
      )}
      {/* Only when there is nothing to keep on screen. Replacing a settled
          multi-line preview with one line moves the confirm button, and this
          card sits in a fixed-row grid so the whole row would oscillate on
          every keystroke burst — the button being moved is the confirm for an
          append-only, unreversible money movement. */}
      {preview === 'pending' && !previewShown.current && (
        <p className="label-s" style={{ marginTop: 6, color: 'var(--ink-3)' }}>Computing preview…</p>
      )}
      {preview === 'error' && (
        <p className="label-s" style={{ marginTop: 6, color: 'var(--oxblood)' }}>PREVIEW UNAVAILABLE.</p>
      )}
      <button className="btn" disabled={busy || !investor || !units || isFutureWhen(when)} onClick={async () => {
        setBusy(true); setErr(null);
        try {
          await api(`/ops/funds/${fundId}/redemptions`, { method: 'POST', body: {
            investor_id: investor, units_redeemed: units,
            ...(when ? { effective_at: new Date(when).toISOString() } : {}),
          }});
          setInvestor(''); setUnits(''); setWhen(''); setPreview(null); await onDone();
        } catch (e: any) {
          setErr(e?.payload?.detail || e?.message || 'Redemption failed.');
        } finally { setBusy(false); }
      }}>Redeem units</button>
    </ActionCard>
  );
}

// Phase 8.b.4.b — crystallization trigger card.
// Wraps POST /ops/funds/{id}/performance-crystallizations. Empty body
// = crystallize at the cadence boundary (annual / quarterly / monthly
// per Fund.crystallization_cadence); a period_end + posted_at let the
// operator override for ad-hoc closings.
function CrystallizeCard({ fundId, onDone, setErr }: any) {
  const [periodEnd, setPeriodEnd] = useState('');
  const [busy, setBusy] = useState(false);
  // 2026-07-04 — disabled for the current demo pass at the operator's
  // request. Not a permanent removal: performance-fee crystallization is
  // built and working (POST /ops/funds/{id}/performance-crystallizations),
  // just not something this round of testing needs. Re-enable by dropping
  // the `disabled` override below once it's back in scope.
  const DEMO_DISABLED = true;
  return (
    <ActionCard title="CRYSTALLIZE PERFORMANCE" desc="Realize accrued perf fees at the cadence boundary.">
      <input className="field-input" type="date" value={periodEnd} placeholder="Period end (optional)"
             disabled={DEMO_DISABLED} onChange={e => setPeriodEnd(e.target.value)} />
      {DEMO_DISABLED && (
        <p className="label-s" style={{ color: 'var(--ink-3)' }}>
          NOT PART OF THIS DEMO — COMING LATER.
        </p>
      )}
      <button className="btn" disabled={DEMO_DISABLED || busy} onClick={async () => {
        setBusy(true); setErr(null);
        try {
          await api(`/ops/funds/${fundId}/performance-crystallizations`, { method: 'POST', body: {
            ...(periodEnd ? { period_end: periodEnd } : {}),
          }});
          setPeriodEnd(''); await onDone();
        } catch (e: any) {
          if (e?.status === 409 && e?.payload?.error_kind === 'period_already_crystallized') {
            setErr('This period is already crystallized.');
          } else {
            setErr(e?.message || 'Crystallization failed.');
          }
        } finally { setBusy(false); }
      }}>Crystallize</button>
    </ActionCard>
  );
}

// Phase 8.b.3.b — record manager-fee payment.
// Wraps POST /ops/funds/{id}/manager-fee-payments. The transaction must
// be pre-recorded as TX_FEE / management on a fund-attached account
// (today via shell; a tx-picker UI is deferred as 8.b.3.c). Inline 400/
// 409 error rendering so the operator sees overdraft + validation
// failures without leaving the page.
function FeePaymentCard({ fundId, baseCurrency, onDone, setErr }: any) {
  const [txId, setTxId] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const ccy = (baseCurrency || 'USD').toUpperCase();
  return (
    <ActionCard title="RECORD FEE PAYMENT" desc="Allocate a swept mgmt-fee transaction across investors.">
      <input className="field-input" type="text" placeholder="Transaction ID (UUID)" value={txId} onChange={e => setTxId(e.target.value)} />
      <input className="field-input" type="number" placeholder={`Amount ${ccy}`} value={amount} onChange={e => setAmount(e.target.value)} />
      <input className="field-input" type="text" placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} />
      <button className="btn" disabled={busy || !txId || !amount} onClick={async () => {
        setBusy(true); setErr(null);
        try {
          await api(`/ops/funds/${fundId}/manager-fee-payments`, { method: 'POST', body: {
            transaction_id: txId.trim(),
            amount_base: amount,
            ...(note.trim() ? { note: note.trim() } : {}),
          }});
          setTxId(''); setAmount(''); setNote(''); await onDone();
        } catch (e: any) {
          if (e?.status === 409) {
            setErr(e?.payload?.detail || 'Fee allocation refused (likely overdraft).');
          } else if (e?.status === 400) {
            setErr(e?.payload?.detail || 'Invalid transaction or amount.');
          } else {
            setErr(e?.message || 'Fee payment failed.');
          }
        } finally { setBusy(false); }
      }}>Record payment</button>
    </ActionCard>
  );
}

// "Fix Valuation" (n8 request, 2026-07): a manager picks a close-of-business
// date, sees the fund's fee accrual for that date, types the total-assets
// figure they read off their own exchange/wallet totals, and watches net NAV
// per share recompute live. Saving lands a fresh strike (if the date has
// never been struck) or an append-only correction (is_restatement=True) if
// it has — the original row is never mutated. No hard-delete affordance:
// FundValuation is append-only by design in this system.
function FixValuationCard({ fundId, baseCurrency, onDone }: any) {
  const [open, setOpen] = useState(false);
  return (
    <ActionCard title="FIX VALUATION" desc="Enter or correct a close-of-business valuation.">
      <button type="button" className="btn" onClick={() => setOpen(true)}>Fix valuation →</button>
      {open && (
        <FixValuationModal
          fundId={fundId}
          baseCurrency={baseCurrency}
          onClose={() => setOpen(false)}
          // FV2.7 — refresh and close are separate: the modal stays open
          // after a save to state what the correction re-based.
          onRefresh={onDone}
        />
      )}
    </ActionCard>
  );
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

// FV2.7 — valuation rows with the FV2.6b correction impact report as an
// inline panel (FundStatements' correct-panel pattern). Read-only: every
// figure is server-computed; the report renders analysis, never mutates.
function ValuationHistorySection({ history, baseCurrency, fundId }: any) {
  const [impactId, setImpactId] = useState<string | null>(null);
  const [impact, setImpact] = useState<any | null | 'pending' | 'error' | 'notfound'>(null);
  const rows = history.slice(0, 14);

  // The open row can vanish from a refreshed history (hidden via the
  // dialog's Delete) — close the panel rather than orphan it.
  useEffect(() => {
    if (impactId && !history.some((h: any) => h.id === impactId)) setImpactId(null);
  }, [history, impactId]);

  useEffect(() => {
    if (!impactId) { setImpact(null); return; }
    let cancelled = false;
    setImpact('pending');
    api<any>(`/ops/funds/${fundId}/valuations/${impactId}/impact`)
      .then(r => { if (!cancelled) setImpact(r); })
      // a transient failure must never read as the domain fact "nothing
      // was affected" — only a 404 (no such valuation) says that.
      .catch((e: any) => { if (!cancelled) setImpact(e?.status === 404 ? 'notfound' : 'error'); });
    return () => { cancelled = true; };
  }, [fundId, impactId]);

  const money = (x: any) => x == null ? '—' : fmtBase(Number(x), baseCurrency, 4);
  const unit = (x: any) => x == null ? '—' : fmtUnitPriceOps(Number(x), baseCurrency);

  return (
    <section style={{ marginTop: 40 }}>
      <div className="kicker-row"><span className="kicker">VALUATIONS · HISTORY</span></div>
      <table className="tbl">
        <thead>
          <tr>
            <th>STRUCK AT</th>
            <th data-align="right">NAV / UNIT</th>
            <th>TYPE</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((h: any) => (
            <tr key={h.id}>
              <td className="num">{fmtDT(h.struck_at)}</td>
              <td data-align="right" className="num">{unit(h.nav_per_unit_base ?? h.nav_per_unit)}</td>
              <td>{h.is_restatement ? <Pill tone="accent">CORRECTION</Pill> : <span className="label-s" style={{ color: 'var(--ink-3)' }}>OFFICIAL</span>}</td>
              <td data-align="right">
                <button
                  type="button" className="btn-link"
                  onClick={() => setImpactId(impactId === h.id ? null : h.id)}
                >{impactId === h.id ? 'Hide impact' : 'View impact'}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {history.length > rows.length && (
        <p className="label-s" style={{ marginTop: 8, color: 'var(--ink-3)' }}>
          SHOWING {rows.length} OF {history.length}
        </p>
      )}

      {impactId && (
        <div style={{ marginTop: 24 }}>
          <div className="kicker-row"><span className="kicker">CORRECTION IMPACT</span></div>
          {impact === 'pending' && <p className="label-s" style={{ color: 'var(--ink-3)' }}>Loading…</p>}
          {impact === 'notfound' && <p className="label-s" style={{ color: 'var(--ink-3)' }}>No impact to show for this valuation.</p>}
          {impact === 'error' && <p className="label-s" style={{ color: 'var(--oxblood)' }}>Could not load the impact report — retry.</p>}
          {impact && typeof impact === 'object' && (
            impact.previous == null ? (
              <p style={{ fontSize: 12, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)' }}>
                No correction chain for this valuation — there is nothing to compare.
              </p>
            ) : (
              <>
                <p style={{ fontSize: 12, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)' }}>
                  Settled figures stand as booked; the deltas below are analysis,
                  never pending mutations. Previously standing{' '}
                  <span className="num" style={{ fontStyle: 'normal' }}>{unit(impact.previous.nav_per_unit_base)}</span>
                  {' '}→ corrected{' '}
                  <span className="num" style={{ fontStyle: 'normal' }}>{unit(impact.corrected.nav_per_unit_base)}</span>.
                </p>
                {impact.deals.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <span className="label-s">DEALS PRICED OFF THE PRIOR FIGURE</span>
                    <table className="tbl" style={{ marginTop: 8 }}>
                      <thead>
                        <tr>
                          <th>INVESTOR</th><th>KIND</th>
                          <th data-align="right">BOOKED</th>
                          <th data-align="right">RECOMPUTED</th>
                          <th data-align="right">DELTA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {impact.deals.slice(0, 14).map((d: any) => (
                          <tr key={d.id}>
                            <td>{d.investor_name}</td>
                            <td><span className="label-s">{d.kind.toUpperCase()}</span></td>
                            <td data-align="right" className="num">
                              {d.kind === 'subscription' ? `${fmtUnits(d.booked_units)} u` : money(d.booked_amount_paid)}
                            </td>
                            <td data-align="right" className="num">
                              {d.kind === 'subscription'
                                ? (d.recomputed_units == null ? '—' : `${fmtUnits(d.recomputed_units)} u`)
                                : money(d.recomputed_amount_paid)}
                            </td>
                            <td data-align="right" className="num">
                              {d.kind === 'subscription'
                                ? (d.units_delta == null ? '—' : `${fmtUnits(d.units_delta)} u`)
                                : signedBase(d.amount_delta, baseCurrency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {impact.fee_days.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <span className="label-s">FEE-ACCRUAL DAYS BASED ON THE PRIOR FIGURE</span>
                    <table className="tbl" style={{ marginTop: 8 }}>
                      <thead>
                        <tr>
                          <th>DATE</th><th>INVESTOR</th>
                          <th data-align="right">BOOKED</th>
                          <th data-align="right">RECOMPUTED</th>
                          <th data-align="right">DELTA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {impact.fee_days.slice(0, 14).map((f: any, i: number) => (
                          <tr key={i}>
                            <td className="num">
                              {f.accrual_date}
                              {f.entry_kind === 'adjustment' && <>{' '}<Pill tone="accent">ADJUSTMENT</Pill></>}
                            </td>
                            <td>{f.investor_name}</td>
                            <td data-align="right" className="num">{money(f.booked_amount_base)}</td>
                            <td data-align="right" className="num">{money(f.recomputed_amount_base)}</td>
                            <td data-align="right" className="num">{f.delta_base == null ? '—' : signedBase(f.delta_base, baseCurrency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {impact.fee_days.length > 14 && (
                      <p className="label-s" style={{ marginTop: 8, color: 'var(--ink-3)' }}>
                        SHOWING 14 OF {impact.fee_days.length}
                      </p>
                    )}
                  </div>
                )}
                {impact.statements.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <span className="label-s">STATEMENTS FROZEN ONTO THE PRIOR FIGURE</span>
                    <table className="tbl" style={{ marginTop: 8 }}>
                      <thead>
                        <tr>
                          <th>PERIOD</th><th>INVESTOR</th><th>ROLE</th>
                          <th data-align="right">BOOKED CLOSE</th>
                          <th data-align="right">CORRECTED CLOSE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {impact.statements.map((st: any) => (
                          <tr key={st.id} style={{ opacity: st.superseded ? 0.5 : 1 }}>
                            <td className="num">{st.period_label}</td>
                            <td>{st.investor_name}</td>
                            <td><span className="label-s">{st.role.toUpperCase()}</span></td>
                            <td data-align="right" className="num">{unit(st.booked_closing_nav)}</td>
                            <td data-align="right" className="num">{unit(st.corrected_closing_nav)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!impact.deals.length && !impact.fee_days.length && !impact.statements.length && (
                  <p className="label-s" style={{ marginTop: 8, color: 'var(--ink-3)' }}>
                    Nothing settled was priced off the corrected figure.
                  </p>
                )}
              </>
            )
          )}
        </div>
      )}
    </section>
  );
}

function FixValuationModal({ fundId, baseCurrency, onClose, onRefresh }: any) {
  const [date, setDate] = useState(todayIsoDate());
  const [preview, setPreview] = useState<any | null | 'pending' | 'error'>(null);
  const [totalAssets, setTotalAssets] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);
  // FV2.6a — the projected unpaid-accrual re-base for the typed total.
  // Isolated from `preview`: a mid-typing 400 must never blank the dialog.
  const [projection, setProjection] = useState<any | null>(null);
  // FV2.7 — after a save the modal stays open to state what was re-based;
  // `saved` holds the POST response (its `rebase` key is the posted twin).
  const [saved, setSaved] = useState<any | null>(null);
  const ccy = (baseCurrency || 'USD').toUpperCase();

  // Live pull: fee accrual + units + the currently-effective valuation (if
  // any) for the picked date. Never a client-side guess — the server
  // computes fee accrual from the same append-only ledgers strike_nav reads.
  useEffect(() => {
    if (!date) { setPreview(null); return; }
    let cancelled = false;
    setPreview('pending');
    api<any>(`/ops/funds/${fundId}/valuations/fix?date=${encodeURIComponent(date)}`)
      .then(r => {
        if (cancelled) return;
        setPreview(r);
        setTotalAssets(r.existing ? r.existing.gross_nav_base : '');
      })
      .catch(() => { if (!cancelled) setPreview('error'); });
    return () => { cancelled = true; };
  }, [fundId, date]);

  // FV2.6a — debounced re-base projection for the typed total (separate
  // effect: the date-keyed one above SETS totalAssets from the response, so
  // sharing an effect would loop). Server-computed; nothing is derived here.
  useEffect(() => {
    setProjection(null);
    const n = Number(totalAssets);
    if (!date || totalAssets === '' || !isFinite(n) || n < 0) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      api<any>(
        `/ops/funds/${fundId}/valuations/fix?date=${encodeURIComponent(date)}` +
        `&total_assets=${encodeURIComponent(totalAssets)}`,
      )
        .then(r => { if (!cancelled) setProjection(r.rebase_projection ?? null); })
        .catch(() => { /* mid-typing 400s are expected; show nothing */ });
    }, 250); // 250ms debounce on total-assets keystrokes
    return () => { cancelled = true; clearTimeout(handle); };
  }, [fundId, date, totalAssets]);

  const feeAccrual = preview && typeof preview === 'object' ? Number(preview.fee_accrual_base) : 0;
  const units = preview && typeof preview === 'object' ? Number(preview.units_outstanding) : 0;
  const netNavPerShare = totalAssets !== '' && units > 0
    ? (Number(totalAssets) - feeAccrual) / units
    : null;

  const submit = async () => {
    setBusy(true); setLocalErr(null);
    try {
      const r = await api<any>(`/ops/funds/${fundId}/valuations/fix`, { method: 'POST', body: {
        date, total_assets: totalAssets, reason: reason.trim(),
      }});
      setSaved(r);          // FV2.7 — hold the modal open to state the outcome
      await onRefresh();
    } catch (e: any) {
      setLocalErr(e?.payload?.detail || e?.message || 'Save failed.');
    } finally { setBusy(false); }
  };

  // "Delete" never removes the row — FundValuation is append-only. It hides
  // this date's valuation (drops it from the normal history/sparkline view);
  // the figures stay in the database for audit. Reuses the Reason field so
  // a hide is attributed exactly like a save.
  const hide = async () => {
    if (!confirm(
      'Hide this valuation? It will no longer appear in the fund’s history. ' +
      'The underlying record is kept for audit — this cannot be truly deleted.',
    )) return;
    setBusy(true); setLocalErr(null);
    try {
      await api(`/ops/funds/${fundId}/valuations/fix`, { method: 'DELETE', body: {
        date, reason: reason.trim(),
      }});
    } catch (e: any) {
      // only the DELETE itself can mean "hide failed" — the refresh below
      // failing must not report a committed hide as a failure.
      setLocalErr(e?.payload?.detail || e?.message || 'Hide failed.');
      setBusy(false);
      return;
    }
    onClose();
    onRefresh().catch(() => {});
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        overflowY: 'auto', padding: '48px 24px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--paper)', color: 'var(--ink)', maxWidth: 480, width: '100%',
          border: 'var(--hairline)', borderColor: 'var(--ink)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '18px 24px', borderBottom: 'var(--hairline)', borderColor: 'var(--rule)',
        }}>
          <span className="label">FIX VALUATION</span>
          <button type="button" className="modal-close" onClick={onClose}>CLOSE</button>
        </div>
        {saved ? (
          /* FV2.7 — post-save confirmation: state what the correction did
             before the dialog goes away. `rebase` is the posted twin of the
             preview's projection (null when nothing required re-basing). */
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p className="label-s">VALUATION SAVED for {date}.</p>
            <div className="num" style={{ fontSize: 20 }}>
              {saved.nav_per_unit_base != null ? fmtUnitPriceOps(Number(saved.nav_per_unit_base), baseCurrency) : '—'}
            </div>
            {saved.rebase ? (
              <p style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)' }}>
                Re-based {saved.rebase.mgmt_days || 0} unpaid management accrual
                day(s)
                {Number(saved.rebase.perf_days) > 0 && ` and ${saved.rebase.perf_days} performance day(s)`}:{' '}
                <span className="num" style={{ fontStyle: 'normal' }}>
                  {signedBase(saved.rebase.total_posted_base, baseCurrency)}
                </span>, posted as a dated adjustment.
                {Number(saved.rebase.total_overpayment_base) !== 0 &&
                  ' A reduction beyond the unpaid balance was reported, not posted — collected fees stand.'}
              </p>
            ) : (
              <p style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)' }}>
                No unpaid accruals required re-basing.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label className="field-label">VALUATION DATE</label>
            <input
              className="field-input" type="date" value={date} max={todayIsoDate()}
              onChange={e => setDate(e.target.value)}
            />
          </div>

          {preview === 'pending' && <p className="label-s" style={{ color: 'var(--ink-3)' }}>Loading…</p>}
          {preview === 'error' && <p className="label-s" style={{ color: 'var(--oxblood)' }}>Could not load this date.</p>}

          {preview && typeof preview === 'object' && (
            <>
              {preview.existing && (
                <p className="label-s" style={{ color: 'var(--ink-3)' }}>
                  {preview.existing.is_restatement ? 'A correction already exists' : 'Already struck'} for this
                  date — saving lands a new append-only correction.
                </p>
              )}
              <div>
                <label className="field-label">VALUATION ({ccy})</label>
                <input
                  className="field-input num" type="number" value={totalAssets}
                  onChange={e => setTotalAssets(e.target.value)}
                  placeholder={`Total assets ${ccy}`}
                />
                <div style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)', marginTop: 4 }}>
                  Gross total assets, before fees — fee accrual is deducted below to compute net NAV.
                </div>
              </div>
              <div>
                <label className="field-label">FEE ACCRUAL ({ccy})</label>
                <input className="field-input num" value={feeAccrual.toFixed(4)} disabled readOnly />
                {/* FV2.4 — make the figure checkable by eye: rate x days.  */}
                <div style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)', marginTop: 4 }}>
                  {Number(preview.fee_rate_annual_pct) > 0
                    ? `${Number(preview.fee_rate_annual_pct).toFixed(2)}% p.a. · ${preview.fee_accrual_days ?? 0} days accrued since inception (ACT/ACT), before fee payments`
                    : feeAccrual !== 0
                      ? 'No management fee — this balance is accrued performance fees.'
                      : 'This fund charges no fees — the accrual is always 0.0000.'}
                </div>
                {preview.fee_basis_reason === 'no_prior_month_end' && (
                  <p style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)', marginTop: 6 }}>
                    No valuation stands for the prior month-end, so this
                    month's accrual has no basis yet — enter the prior
                    month-end first, then revisit this date.
                  </p>
                )}
                {preview.fee_basis_reason === 'prior_month_end_restated' && (
                  <p style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)', marginTop: 6 }}>
                    The prior month-end's standing figure is a correction,
                    which is not a fee basis — re-enter that date and it will
                    land as the official cut, replacing the correction.
                  </p>
                )}
              </div>
              <div style={{ padding: '12px 0', borderTop: 'var(--hairline)', borderColor: 'var(--rule)' }}>
                <span className="label-s">NET NAV PER SHARE</span>
                <div className="num" style={{ fontSize: 20, marginTop: 4 }}>
                  {netNavPerShare !== null ? fmtUnitPriceOps(netNavPerShare, baseCurrency) : '—'}
                </div>
                {/* FV2.4 — a bare dash has three different causes; say which. */}
                {units === 0 && (
                  <p style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)', marginTop: 6 }}>
                    No units outstanding on this date — a unit price does
                    not apply.{feeAccrual === 0 && ' Zero fee here is arithmetically correct, not an error.'}
                  </p>
                )}
                {units > 0 && totalAssets === '' && (
                  <p className="label-s" style={{ marginTop: 4, color: 'var(--ink-3)' }}>
                    Enter total assets above to compute.
                  </p>
                )}
                {/* FV2.6a — the projected unpaid-accrual re-base (server-
                    computed; preview == save by shared backend code). */}
                {projection && (
                  <>
                    <p style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)', marginTop: 6 }}>
                      This correction re-bases {projection.mgmt_days || 0} unpaid
                      management accrual day(s)
                      {Number(projection.perf_days) > 0 && ` and ${projection.perf_days} performance day(s)`}:{' '}
                      <span className="num" style={{ fontStyle: 'normal' }}>
                        {signedBase(projection.total_posted_base, baseCurrency)}
                      </span>, posted as a dated adjustment — no historical day is rewritten.
                    </p>
                    {Number(projection.total_overpayment_base) !== 0 && (
                      <p style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)', marginTop: 6 }}>
                        The reduction is capped at each investor's unpaid balance —{' '}
                        <span className="num" style={{ fontStyle: 'normal' }}>
                          {signedBase(projection.total_overpayment_base, baseCurrency)}
                        </span>{' '}
                        beyond it is reported, not posted. Fees already collected stand.
                      </p>
                    )}
                    {projection.forward_basis_unchanged && (
                      <p style={{ fontSize: 11, fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)', marginTop: 6 }}>
                        This correction does not change the current month's fee
                        basis — to make it the basis, delete the standing figure
                        and re-enter this date as the official cut.
                      </p>
                    )}
                  </>
                )}
              </div>
              <div>
                <label className="field-label">REASON</label>
                <textarea
                  className="field-input" rows={2} value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Why is this valuation being entered or corrected?"
                />
              </div>
              {localErr && <p className="label-s" style={{ color: 'var(--oxblood)' }}>{localErr}</p>}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                  type="button" className="btn-link" style={{ color: 'var(--oxblood)' }}
                  disabled={busy || !preview.existing || !reason.trim()}
                  onClick={hide}
                >Delete</button>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="button" className="btn-link" onClick={onClose}>Cancel</button>
                  <button
                    className="btn" disabled={busy || !totalAssets || !reason.trim()}
                    onClick={submit}
                  >Save</button>
                </div>
              </div>
            </>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

// ─── Investor (client) view ─────────────────────────────────────────────────
function InvestorView() {
  const [params] = useSearchParams();
  const clientId = params.get('client_id');
  // positions: list of fund positions (one per fund the investor holds units in).
  // Phase 8.b.1 — multi-fund correctness: the response is `{positions: [...]}`
  // even for the single-fund case. Investors with units in N funds see all N
  // and pick via the switcher when N > 1; 'none' means 204 (no positions);
  // 'error' is a network / server failure (distinct so we don't misread an
  // outage as "no positions held").
  const [positions, setPositions] = useState<any[] | null | 'none' | 'error'>(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  // Phase 8.b.6.b — investor's own daily accruals (the endpoint silently
  // pins role=client to their linked investor, so the data is self-scoped
  // by the backend; no client_id needed and no cross-investor leak).
  const [accruals, setAccruals] = useState<any | null>(null);

  useEffect(() => {
    const url = '/portfolio/fund' + (clientId ? `?client_id=${clientId}` : '');
    api<{ positions: any[] }>(url)
      .then(r => {
        if (!r || !r.positions || r.positions.length === 0) { setPositions('none'); return; }
        setPositions(r.positions);
        setSelectedIdx(0);
      })
      .catch(() => setPositions('error'));
  }, [clientId]);

  // Fetch accruals once we know which fund is selected. Refetch when the
  // selected fund changes — accruals are per-fund.
  useEffect(() => {
    if (!Array.isArray(positions) || !positions[selectedIdx]) return;
    const fundId = positions[selectedIdx].fund_id;
    Promise.all([
      api<any>(`/ops/funds/${fundId}/accruals?kind=management&limit=20`),
      api<any>(`/ops/funds/${fundId}/accruals?kind=performance&limit=20`),
    ])
      .then(([m, p]) => setAccruals({ management: m, performance: p }))
      .catch(() => setAccruals(null));
  }, [positions, selectedIdx]);

  if (positions === null) return <Loading />;
  if (positions === 'none') return (
    <div className="view container">
      <PageHeader kicker="YOUR FUND POSITION" title={<><em>Units.</em></>} />
      <p className="serif-body" style={{ fontSize: 16, marginTop: 24 }}>You do not hold any fund units.</p>
      {/* Phase 9.b — next-step. Closes the audit gap where investors */}
      {/* landing on /fund with zero units had no Contact pointer.    */}
      <p className="serif-body" style={{ fontSize: 14, marginTop: 16, color: 'var(--ink-3)' }}>
        Interested in our pooled fund?{' '}
        <Link to="/portal/contact" style={{ color: 'var(--ink)' }}>Contact us</Link>
        {' '}to discuss a subscription.
      </p>
      <p className="label-s" style={{ marginTop: 24 }}>
        <Link to="/dashboard" style={{ color: 'var(--ink-3)' }}>← BACK TO DASHBOARD</Link>
      </p>
    </div>
  );
  if (positions === 'error') return (
    <div className="view container">
      <PageHeader kicker="YOUR FUND POSITION" title={<><em>Units.</em></>} />
      <p className="serif-body" style={{ fontSize: 16, marginTop: 24 }}>
        Unable to load your fund position. Try refreshing in a moment.
      </p>
    </div>
  );

  const pos = positions[selectedIdx];
  // Phase 7: pick base-currency figures for non-USD funds.
  const isUSD = (pos.base_currency || 'USD') === 'USD';
  // RULE-FEE-WINDUP fires only on the last unitholder out, so an unconditional
  // column would be a wall of zeroes on every ordinary statement of activity.
  // Rendered only when a row actually carries one — the same rule the statement
  // PDF applies.
  const showWindupFee = (pos.redemptions || []).some(
    (r: any) => Number(r.final_mgmt_fee_base ?? r.final_mgmt_fee_usd ?? 0) !== 0);
  // OB.3 — the fund has not yet struck a NAV covering this investor's
  // subscription. value / NAV / P&L come back null; show a calm "pending"
  // treatment rather than a red −100% loss. Units + contributed stay real.
  const pending = pos.valuation_pending === true;
  const pnl = pending ? 0 : Number(isUSD ? pos.unrealized_pnl_usd : pos.unrealized_pnl_base);
  return (
    <div className="view container">
      <PageHeader kicker={`${pos.fund_name.toUpperCase()} · YOUR POSITION`} title={<><em>Units.</em></>}
        meta={<span className="label-s">AS OF {fmtDT(pos.as_of)}</span>} />
      {positions.length > 1 && (
        <label className="label-s" style={{ marginTop: 16, display: 'inline-block' }}>
          FUND&nbsp;
          <select
            className="num"
            value={selectedIdx}
            onChange={e => setSelectedIdx(Number(e.target.value))}
          >
            {positions.map((p, i) => (
              <option key={p.fund_id || p.fund_name} value={i}>{p.fund_name}</option>
            ))}
          </select>
        </label>
      )}
      {pending && (
        <p className="serif-body" style={{ marginTop: 16, fontSize: 15, color: 'var(--ink-2)', maxWidth: '60ch' }}>
          Valuation pending. Your subscription is recorded; your units will be
          valued at the fund's next valuation.
        </p>
      )}
      <div className="agg-strip" style={{ ['--cols' as any]: 4 } as any}>
        {/* TRES Phase 7 / n8 cutover: investor view renders in the fund's */}
        {/* base currency too. /portfolio/fund (7.b) sends parallel `_base` */}
        {/* keys; the dispatcher picks the right glyph per fund.            */}
        <div className="agg-cell"><Stat label="UNITS HELD" value={fmtUnits(pos.units)} /></div>
        <div className="agg-cell"><Stat label="NAV PER UNIT" value={pending ? '—' : fmtUnitPrice(Number(isUSD ? pos.nav_per_unit : pos.nav_per_unit_base), pos.base_currency)} /></div>
        <div className="agg-cell"><Stat label="CURRENT VALUE" value={pending ? '—' : fmtBase(Number(isUSD ? pos.value_usd : pos.value_base), pos.base_currency)} sub={`CONTRIBUTED ${fmtBase(Number(isUSD ? pos.contributed_usd : pos.contributed_base), pos.base_currency)}`} /></div>
        <div className="agg-cell"><Stat label="UNREALIZED P&L" value={pending ? '—' : <span className={pnl >= 0 ? 'pos' : 'neg'}>{fmtBase(pnl, pos.base_currency)}</span>} sub={pending ? 'AWAITING FIRST VALUATION' : `AVG COST ${fmtUnitPrice(Number(isUSD ? pos.avg_cost_per_unit : pos.avg_cost_per_unit_base), pos.base_currency)}/UNIT`} /></div>
      </div>
      <section style={{ marginTop: 40 }}>
        <div className="kicker-row"><span className="kicker">YOUR SUBSCRIPTIONS</span></div>
        <table className="tbl" style={{ marginTop: 12 }}>
          <thead><tr><th>EFFECTIVE (UTC)</th><th data-align="right">AMOUNT</th><th data-align="right">NAV / UNIT</th><th data-align="right">UNITS</th></tr></thead>
          <tbody>
            {pos.subscriptions.map((s: any, i: number) => (
              <tr key={i}>
                <td className="num" style={{ fontSize: 12 }}>{fmtDT(s.effective_at)}</td>
                <td data-align="right" className="num">{fmtBase(Number(isUSD ? s.amount_usd : s.amount_base), pos.base_currency)}</td>
                <td data-align="right" className="num">{fmtUnitPrice(Number(isUSD ? s.nav_per_unit : s.nav_per_unit_base), pos.base_currency)}</td>
                <td data-align="right" className="num">{fmtUnits(s.units_issued)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {/* Phase 8.b.10 — NAV/unit sparkline for the investor view.       */}
      {/* Fund-public series; identical across investors.                 */}
      {Array.isArray(pos.nav_history) && pos.nav_history.length >= 2 && (
        <section style={{ marginTop: 40 }}>
          <div className="kicker-row"><span className="kicker">NAV PER UNIT · HISTORY</span></div>
          <NavSparkline series={pos.nav_history.map((h: any) => ({ x: h.struck_at, y: Number(isUSD ? h.nav_per_unit : h.nav_per_unit_base) }))} currency={pos.base_currency || 'USD'} />
        </section>
      )}

      {/* Phase 8.b.9 — fund-public terms surfaced read-only. Identical  */}
      {/* across investors; investor's contract reference.                */}
      {pos.terms && (
        <section style={{ marginTop: 40 }}>
          <div className="kicker-row"><span className="kicker">FUND TERMS</span></div>
          <div className="agg-strip" style={{ ['--cols' as any]: 4, marginTop: 12 } as any}>
            <div className="agg-cell"><Stat label="MGMT FEE" value={`${pos.terms.management_fee_annual_pct}% / YEAR`} /></div>
            <div className="agg-cell"><Stat label="PERF FEE" value={`${pos.terms.performance_fee_pct}%`} sub={pos.terms.hurdle_kind === 'none' ? 'NO HURDLE' : `${pos.terms.hurdle_annual_pct}% HURDLE`} /></div>
            <div className="agg-cell"><Stat label="CRYSTALLIZES" value={String(pos.terms.crystallization_cadence || '').toUpperCase()} /></div>
            <div className="agg-cell"><Stat label="DEALING" value={String(pos.terms.dealing_basis || '').toUpperCase().replace(/_/g, ' ')} sub={pos.terms.dealing_tz} /></div>
          </div>
        </section>
      )}

      {/* Phase 8.b.6.b — investor's own accruals (mgmt + perf).         */}
      {/* Backend silently pins role=client to linked investor; no       */}
      {/* cross-investor data leaks through.                             */}
      {accruals && accruals.management && accruals.management.total > 0 && (
        <section style={{ marginTop: 40 }}>
          <div className="kicker-row"><span className="kicker">YOUR MGMT-FEE ACCRUALS (LAST 20 DAYS)</span></div>
          <table className="tbl" style={{ marginTop: 12 }}>
            <thead><tr><th>DATE</th><th data-align="right">BASIS</th><th data-align="right">RATE</th><th data-align="right">ACCRUED</th></tr></thead>
            <tbody>
              {accruals.management.rows.map((row: any) => (
                <tr key={row.id}>
                  <td className="num" style={{ fontSize: 12 }}>{row.accrual_date}</td>
                  <td data-align="right" className="num">{fmtBase(Number(row.basis_base), pos.base_currency)}</td>
                  <td data-align="right" className="num">{row.rate_annual}%</td>
                  <td data-align="right" className="num">{fmtBase(Number(row.accrued_amount_base), pos.base_currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {accruals && accruals.performance && accruals.performance.total > 0 && (
        <section style={{ marginTop: 32 }}>
          <div className="kicker-row"><span className="kicker">YOUR PERF-FEE ACCRUALS (LAST 20 DAYS)</span></div>
          <table className="tbl" style={{ marginTop: 12 }}>
            <thead><tr><th>DATE</th><th data-align="right">UNITS</th><th data-align="right">NAV / UNIT</th><th data-align="right">HWM</th><th data-align="right">ACCRUED</th></tr></thead>
            <tbody>
              {accruals.performance.rows.map((row: any) => (
                <tr key={row.id}>
                  <td className="num" style={{ fontSize: 12 }}>{row.accrual_date}</td>
                  <td data-align="right" className="num">{fmtUnits(row.units_held)}</td>
                  <td data-align="right" className="num">{fmtUnitPrice(Number(row.nav_per_unit_base), pos.base_currency)}</td>
                  <td data-align="right" className="num">{fmtUnitPrice(Number(row.hwm_per_unit_base), pos.base_currency)}</td>
                  <td data-align="right" className="num">{fmtBase(Number(row.accrued_amount_base), pos.base_currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Phase 8.b.2 — investor's own redemption history. Only renders */}
      {/* when redemptions exist so the empty case stays clean.          */}
      {Array.isArray(pos.redemptions) && pos.redemptions.length > 0 && (
        <section style={{ marginTop: 40 }}>
          <div className="kicker-row"><span className="kicker">YOUR REDEMPTIONS</span></div>
          <table className="tbl" style={{ marginTop: 12 }}>
            <thead><tr><th>EFFECTIVE (UTC)</th><th data-align="right">UNITS</th><th data-align="right">NAV / UNIT</th><th data-align="right">PAID</th><th data-align="right">REDEMPTION FEE</th>{showWindupFee && <th data-align="right">MGMT FEE WITHHELD</th>}</tr></thead>
            <tbody>
              {pos.redemptions.map((r: any, i: number) => (
                <tr key={i}>
                  <td className="num" style={{ fontSize: 12 }}>{fmtDT(r.effective_at)}</td>
                  <td data-align="right" className="num">{fmtUnits(r.units_redeemed)}</td>
                  <td data-align="right" className="num">{fmtUnitPrice(Number(isUSD ? r.nav_per_unit : r.nav_per_unit_base), pos.base_currency)}</td>
                  <td data-align="right" className="num">{fmtBase(Number(isUSD ? r.amount_usd_paid : r.amount_base_paid), pos.base_currency)}</td>
                  <td data-align="right" className="num">{fmtBase(Number(isUSD ? r.redemption_fee_usd : r.redemption_fee_base), pos.base_currency)}</td>
                  {showWindupFee && <td data-align="right" className="num">{fmtBase(Number(isUSD ? r.final_mgmt_fee_usd : r.final_mgmt_fee_base), pos.base_currency)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function Loading() {
  return <div className="view container"><p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Loading…</p></div>;
}
